import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const loc = useLocation();
  const nav = useNavigate();
  const token = useMemo(() => {
    const params = new URLSearchParams(loc.search);
    return params.get("token") || "";
  }, [loc.search]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [linkError, setLinkError] = useState("");

  const missingToken = !token || token.length < 16;

  const submit = async (e) => {
    e.preventDefault();
    if (missingToken) {
      setLinkError("This reset link is invalid or has expired.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Please log in.");
      setTimeout(() => {
        nav("/login");
      }, 2000);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 400 && typeof detail === "string") {
        msg = detail;
      } else if (!err?.response) {
        msg = "Server unavailable. Please try again shortly.";
      } else {
        msg = "Could not reset password. Please try again.";
      }
      setLinkError(msg);
      toast.error(msg);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight mb-2">Choose a new password</h1>
          <p className="text-sm text-zinc-500">
            Enter a new password for your PineForge.AI account.
          </p>
        </div>
        <div className="bg-[#12141C] border border-[#1E212B] rounded-xl p-8">
          {done ? (
            <div data-testid="reset-success" className="space-y-4">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">Password updated</span>
              </div>
              <p className="text-sm text-zinc-400">
                You can now log in with your new password.
              </p>
              <Link
                to="/login"
                data-testid="reset-back-to-login"
                className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Go to log in
              </Link>
            </div>
          ) : missingToken ? (
            <div data-testid="reset-missing-token" className="space-y-4">
              <p className="text-sm text-rose-300">This reset link is missing or malformed.</p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300"
              >
                Request a new reset link
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">New password</Label>
                <Input
                  id="password"
                  data-testid="reset-password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 bg-[#090A0F] border-[#1E212B] focus:border-blue-500 focus-visible:ring-blue-500/30 h-11"
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">Confirm password</Label>
                <Input
                  id="confirm"
                  data-testid="reset-confirm"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5 bg-[#090A0F] border-[#1E212B] focus:border-blue-500 focus-visible:ring-blue-500/30 h-11"
                />
              </div>
              {linkError ? (
                <p data-testid="reset-error" className="text-sm text-rose-300">{linkError}</p>
              ) : null}
              <Button
                type="submit"
                disabled={busy}
                data-testid="reset-submit"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</>
                ) : (
                  "Update password"
                )}
              </Button>
              <div className="pt-2 text-center">
                <Link
                  to="/login"
                  data-testid="reset-back-link"
                  className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-300"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to log in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
